import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, describeApiError } from '../lib/api';
import { MERGE_FIELDS, type CommsTemplate } from '../../../shared/comms';

const slug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'template';

/** New templates take an id from their name, made unique against the rest. */
function withIds(list: CommsTemplate[]): CommsTemplate[] {
  const taken = new Set(list.filter((t) => !t.id.startsWith('new-template')).map((t) => t.id));
  return list.map((t) => {
    if (!t.id.startsWith('new-template')) return t;
    let id = slug(t.name);
    for (let n = 2; taken.has(id); n++) id = `${slug(t.name).slice(0, 36)}-${n}`;
    taken.add(id);
    return { ...t, id };
  });
}

/** Admin editor for the email and letter templates used by "Write to client". */
export function CommsTemplatesPanel({ templates }: { templates: CommsTemplate[] }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<CommsTemplate[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const list = draft ?? templates;
  const edit = (id: string, patch: Partial<CommsTemplate>) => setDraft(list.map((t) => (t.id === id ? { ...t, ...patch } : t)));

  async function save(next: CommsTemplate[], done: string) {
    setStatus(null);
    try {
      await api('/api/settings', { method: 'PUT', body: JSON.stringify({ commsTemplates: next }) });
      await qc.invalidateQueries({ queryKey: ['settings'] });
      setDraft(null);
      setStatus(done);
    } catch (e) { setStatus(describeApiError(e, 'Save failed')); }
  }

  function add() {
    let id = 'new-template';
    for (let n = 2; list.some((t) => t.id === id); n++) id = `new-template-${n}`;
    setDraft([...list, { id, name: 'New template', subject: '', body: 'Dear {{salutation}},\n\n\n\nKind regards,\n{{adviserName}}' }]);
    setOpenId(id);
  }

  return (
    <div className="qa-card p-5 mb-6" data-testid="comms-templates">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
        <p className="qa-eyebrow">Email and letter templates</p>
        <p className="text-[11px] text-ink-mute">Fields: {MERGE_FIELDS.map((f) => `{{${f.key}}}`).join(' ')}</p>
      </div>
      <ul className="divide-y divide-hairline-paper">
        {list.map((t) => (
          <li key={t.id} className="py-2">
            <div className="flex items-center gap-3">
              <button type="button" className="text-sm font-medium text-ink hover:text-rust text-left flex-1" aria-expanded={openId === t.id} onClick={() => setOpenId(openId === t.id ? null : t.id)}>{t.name}</button>
              <span className="text-xs text-ink-mute truncate max-w-[50%]">{t.subject}</span>
              <button type="button" className="text-xs text-ink-mute hover:underline" aria-label={`Delete template ${t.name}`} onClick={() => setDraft(list.filter((x) => x.id !== t.id))}>Delete</button>
            </div>
            {openId === t.id && (
              <div className="grid grid-cols-1 gap-2 mt-2 text-xs">
                <label>Name<input className="qa-input block w-full mt-1 text-sm" aria-label="Template name" value={t.name}
                  onChange={(e) => edit(t.id, { name: e.target.value })} /></label>
                <label>Subject<input className="qa-input block w-full mt-1 text-sm" aria-label="Template subject" value={t.subject} onChange={(e) => edit(t.id, { subject: e.target.value })} /></label>
                <label>Body<textarea className="qa-input block w-full mt-1 text-sm qa-mono" rows={8} aria-label="Template body" value={t.body} onChange={(e) => edit(t.id, { body: e.target.value })} /></label>
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-hairline-paper">
        <button type="button" className="btn-primary text-sm" disabled={!draft || list.some((t) => !t.name.trim())}
          onClick={() => save(withIds(list), 'Templates saved.')}>Save templates</button>
        <button type="button" className="btn-ghost text-xs" onClick={add}>Add template</button>
        {draft && <button type="button" className="btn-ghost text-xs" onClick={() => setDraft(null)}>Discard</button>}
        <button type="button" className="btn-ghost text-xs" onClick={() => { if (window.confirm('Put back the standard templates? Your edits are replaced.')) void save([], 'Standard templates restored.'); }}>Restore standard templates</button>
        {status && <span role="status" className="text-xs text-ink-soft">{status}</span>}
      </div>
      <p className="text-[11px] text-ink-mute mt-2">Advisers pick these from the envelope button on a client record.</p>
    </div>
  );
}
